/**
 * Complete Production-Grade Storefront CMS Customizer Route — Module 10.
 */

import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { siteConfig } from "@/config/site";
import { Layout, Save, Check, Megaphone, Store } from "lucide-react";
import {
  fetchCmsConfigFromSupabase,
  saveCmsConfigToSupabase,
  type StorefrontCmsConfig,
} from "@/features/admin/cms/store/admin-cms-store";

export const Route = createFileRoute("/admin/cms")({
  head: () => ({
    meta: [{ title: `CMS & Storefront Customizer — Admin — ${siteConfig.name}` }],
  }),
  component: AdminCmsPage,
});

function AdminCmsPage() {
  const [config, setConfig] = useState<StorefrontCmsConfig>({
    announcementBarText: "Festive Season Sale — Up to 50% Off Luxury Botanicals + Free Shipping",
    announcementBarActive: true,
    heroHeadline: "Artisanal Botanicals for Contemporary Interiors",
    heroSubheadline:
      "Hand-finished artificial bonsai trees & foliage crafted for timeless elegance.",
    footerTagline: "Handcrafted Luxury Artificial Botanicals",
    contactEmail: "concierge@asterdecorz.com",
    contactPhone: "+91 98765 43210",
  });
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  useEffect(() => {
    fetchCmsConfigFromSupabase().then((data) => {
      if (data) setConfig(data);
    });
  }, []);

  const triggerToast = (msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3000);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await saveCmsConfigToSupabase(config);
      triggerToast("Storefront CMS configuration saved successfully!");
    } catch (err: unknown) {
      console.error("Failed to save CMS config:", err);
      const message = (err as { message?: string }).message;
      triggerToast(`Failed to save CMS config in database: ${message || "Database error"}`);
    }
  };

  return (
    <div className="space-y-6 pb-16">
      {toastMsg && (
        <div className="fixed top-20 right-6 z-50 bg-foreground text-background text-xs font-semibold px-4 py-3 rounded-md shadow-xl flex items-center gap-2 animate-in fade-in duration-150">
          <Check className="h-4 w-4 text-emerald-400" />
          <span>{toastMsg}</span>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-6">
        <div>
          <p className="eyebrow text-xs text-primary font-semibold">Storefront Customization</p>
          <h1 className="mt-1 font-display text-3xl text-foreground">CMS & Storefront Content</h1>
          <p className="mt-1 text-xs text-muted-foreground">
            Customize top announcement bar text, homepage copy, footer taglines, and support contact
            details.
          </p>
        </div>
      </div>

      <form onSubmit={handleSave} className="max-w-3xl space-y-6 text-xs">
        {/* Top Announcement Bar */}
        <div className="bg-card border border-border p-6 rounded-lg space-y-4 shadow-2xs">
          <div className="flex items-center justify-between border-b pb-3">
            <div className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-primary" />
              <h2 className="font-display text-lg font-semibold">Top Announcement Ticker Bar</h2>
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
              <input
                type="checkbox"
                checked={config.announcementBarActive}
                onChange={(e) => setConfig({ ...config, announcementBarActive: e.target.checked })}
                className="rounded border-border text-primary focus:ring-primary"
              />
              Active Bar
            </label>
          </div>

          <div>
            <label className="eyebrow text-[11px] block mb-1.5">Announcement Ticker Text</label>
            <input
              type="text"
              value={config.announcementBarText}
              onChange={(e) => setConfig({ ...config, announcementBarText: e.target.value })}
              className="min-h-10 w-full border border-border bg-background px-3 text-xs outline-none focus:border-primary rounded"
            />
          </div>
        </div>

        {/* Hero Section Copy */}
        <div className="bg-card border border-border p-6 rounded-lg space-y-4 shadow-2xs">
          <div className="flex items-center gap-2 border-b pb-3">
            <Store className="h-5 w-5 text-primary" />
            <h2 className="font-display text-lg font-semibold">Homepage Hero Typography</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="eyebrow text-[11px] block mb-1.5">Main Hero Title</label>
              <input
                type="text"
                value={config.heroHeadline}
                onChange={(e) => setConfig({ ...config, heroHeadline: e.target.value })}
                className="min-h-10 w-full border border-border bg-background px-3 text-xs outline-none focus:border-primary rounded font-display text-sm"
              />
            </div>

            <div>
              <label className="eyebrow text-[11px] block mb-1.5">Main Hero Subtitle</label>
              <textarea
                rows={2}
                value={config.heroSubheadline}
                onChange={(e) => setConfig({ ...config, heroSubheadline: e.target.value })}
                className="w-full border border-border bg-background p-3 text-xs outline-none focus:border-primary rounded"
              />
            </div>
          </div>
        </div>

        {/* Footer & Support Contact */}
        <div className="bg-card border border-border p-6 rounded-lg space-y-4 shadow-2xs">
          <h2 className="font-display text-lg font-semibold border-b pb-3">
            Footer & Concierge Support Information
          </h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="eyebrow text-[11px] block mb-1.5">Support Email</label>
              <input
                type="email"
                value={config.contactEmail}
                onChange={(e) => setConfig({ ...config, contactEmail: e.target.value })}
                className="min-h-10 w-full border border-border bg-background px-3 text-xs font-mono outline-none focus:border-primary rounded"
              />
            </div>

            <div>
              <label className="eyebrow text-[11px] block mb-1.5">Concierge Phone</label>
              <input
                type="text"
                value={config.contactPhone}
                onChange={(e) => setConfig({ ...config, contactPhone: e.target.value })}
                className="min-h-10 w-full border border-border bg-background px-3 text-xs font-mono outline-none focus:border-primary rounded"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="px-6 py-2.5 bg-primary text-primary-foreground font-semibold text-xs uppercase tracking-wider rounded hover:opacity-90 transition-opacity shadow-xs flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Save Storefront CMS Settings
          </button>
        </div>
      </form>
    </div>
  );
}
